import React, { useState, useEffect, forwardRef, useRef, memo } from "react"; // MODIFIED: Imported 'memo'
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Brain, BarChart3, RotateCcw, Download, LineChart, DollarSign, Phone, Factory, Users } from "lucide-react";
import { InfoTooltip, LoadingSpinner } from './UIComponents';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

// MODIFIED: Wrapped the entire component definition in React.memo for stability
const PathsChart = memo(forwardRef(({ completedPaths }, ref) => {
    if (!completedPaths || completedPaths.length === 0) return null;
    const [activeMetric, setActiveMetric] = useState('cash');
    const chartData = {
        labels: Array.from({ length: 7 }, (_, i) => `Turn ${i}`),
        datasets: completedPaths.map((path, index) => {
            const colors = ['#3b82f6', '#22c55e', '#ef4444', '#f97316', '#a855f7', '#ec4899', '#14b8a6', '#64748b'];
            const color = colors[index % colors.length];
            return {
                label: `Path #${index + 1}`,
                data: path.nodes.map(node => activeMetric === 'cash' ? node.resources[activeMetric] / 1000000 : node.resources[activeMetric]),
                borderColor: color, backgroundColor: `${color}33`, tension: 0.1, pointRadius: 5,
                pointBorderColor: '#fff', pointBorderWidth: 2, pointHoverRadius: 7,
            };
        })
    };
    
    const metricConfig = {
        cash: { label: 'Cash Reserves ($M)', icon: DollarSign },
        reputation: { label: 'Reputation', icon: Phone },
        operational_capacity: { label: 'Operations', icon: Factory },
        stakeholder_confidence: { label: 'Confidence', icon: Users }
    };
    
    const chartTitle = completedPaths.length > 1 ? 'Path Metric Comparison' : 'Metric Trajectory';
    
    const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' }, title: { display: true, text: chartTitle } }, scales: { y: { beginAtZero: activeMetric !== 'cash', ticks: { callback: function(value) { return activeMetric === 'cash' ? `$${value}M` : `${value}%`; } } } } };
    
    return (
        <div>
            <div className="flex justify-start items-center mb-4">
                <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
                    {Object.entries(metricConfig).map(([key, { label, icon: Icon }]) => (
                        <button key={key} onClick={() => setActiveMetric(key)} className={`flex items-center px-3 py-1 text-sm rounded-md transition-colors ${activeMetric === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`}>
                            <Icon className="w-4 h-4 mr-1.5" />
                            {label.split(' ')[0]}
                        </button>
                    ))}
                </div>
            </div>
            {/* MODIFIED: The ref is now passed to the Line component */}
            <div className="h-64 relative"><Line ref={ref} options={options} data={chartData} devicePixelRatio={2} /></div>
        </div>
    );
}));

const ClickableRecommendation = ({ text, onNavigate }) => {
    if (!text) return null;
    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
                a: ({ node, ...props }) => {
                    if (props.href && props.href.startsWith('path://')) {
                        const pathNumber = parseInt(props.href.substring(7), 10);
                        if (!isNaN(pathNumber)) {
                            return (
                                <button
                                    className="inline-flex items-center font-bold text-blue-600 hover:text-blue-800 hover:underline"
                                    onClick={() => onNavigate(pathNumber)}
                                >
                                    {props.children}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                                </button>
                            );
                        }
                    }
                    return <a href={props.href} target="_blank" rel="noopener noreferrer">{props.children}</a>;
                }
            }}
        >
            {text}
        </ReactMarkdown>
    );
};

const PathComparison = ({ comparison, isLoading, onExport, completedPaths, onNavigate }) => {
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
            <div className="flex justify-between items-start">
                <div className="flex items-start gap-2"> <BarChart3 className="w-6 h-6 text-indigo-500 mr-1 flex-shrink-0 mt-1" /> <div> <h3 className="text-xl font-semibold text-gray-900 mb-1"> Comparative Analysis & Recommendation </h3> <p className="text-gray-600">AI-generated comparison of all completed paths.</p> </div> <InfoTooltip>This report is generated after completing at least two paths. It compares the final outcomes and provides a strategic recommendation.</InfoTooltip> </div>
                {!isLoading && completedPaths.length > 1 && (<button onClick={onExport} className="flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors" title="Export simulation report as a PDF file"><Download className="w-4 h-4 mr-2" />Export Report</button>)}
            </div>
            <div className="min-h-[10rem] relative">
                {isLoading ? ( <div className="absolute inset-0 flex items-center justify-center bg-white z-10"><LoadingSpinner /></div> ) : (
                    <div className="prose prose-sm max-w-none border-t pt-4">
                        <ClickableRecommendation text={comparison} onNavigate={onNavigate} />
                    </div>
                )}
            </div>
        </div>
    );
};

const ComparisonPlaceholder = ({ onPlayAgain }) => {
    return ( <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-6 text-center"><div className="flex justify-center mb-4"><BarChart3 className="w-8 h-8 text-gray-400" /></div><h3 className="text-lg font-semibold text-gray-800 mb-2">Unlock Comparative Analysis</h3><p className="text-gray-600 mb-4 max-w-md mx-auto">Complete one more path in this scenario to generate an AI-powered comparison of your strategic choices and outcomes.</p><button onClick={onPlayAgain} className="flex items-center justify-center mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"><RotateCcw className="w-4 h-4 mr-2" />Start a New Path</button></div> );
};

export const GameCompletionSummary = ({ completedPaths, scenarioTitle, updatePathWithSummary, currentNodeId, onPlayAgain, comparisonData, rawComparisonData, isComparisonLoading, onExport, chartRef, onNavigateToPath, kpiPriorities }) => {
    const [isLoading, setIsLoading] = useState(false);
    const currentPath = completedPaths.find(path => path.nodes[path.nodes.length - 1].id === currentNodeId);
    const fetchingSummaryRef = useRef(new Set());
    const [visibleSections, setVisibleSections] = useState({ debrief: false, chart: false, comparison: false });
    
    const isDebriefLoading = isLoading && currentPath?.summary === null;

    useEffect(() => {
        setVisibleSections({ debrief: false, chart: false, comparison: false });
    
        if (currentPath?.summary === null) {
            return;
        }

        const timer1 = setTimeout(() => setVisibleSections(prev => ({ ...prev, debrief: true })), 50);
        const timer2 = setTimeout(() => setVisibleSections(prev => ({ ...prev, chart: true })), 250);
        const timer3 = setTimeout(() => setVisibleSections(prev => ({ ...prev, comparison: true })), 450);
    
        return () => { clearTimeout(timer1); clearTimeout(timer2); clearTimeout(timer3); };
    }, [currentNodeId, currentPath?.summary]);

    useEffect(() => {
        const generateSummary = async () => {
            if (currentPath && currentPath.summary === null) {
                if (fetchingSummaryRef.current.has(currentPath.id)) return;
                setIsLoading(true);
                fetchingSummaryRef.current.add(currentPath.id);
                const pathPayload = currentPath.nodes.filter(node => node.turn > 0).map(node => ({ turn: node.turn, decision: node.decision.text, consequences: node.decision.consequences, final_resources: currentPath.finalResources }));
                try {
                    const response = await fetch("/api/generate-summary", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ path: pathPayload, scenarioTitle, priorities: kpiPriorities }), });
                    if (!response.ok) throw new Error(`API request failed`);
                    const data = await response.json();
                    updatePathWithSummary(currentPath.id, data.summary);
                } catch (error) {
                    updatePathWithSummary(currentPath.id, "Error generating AI summary.");
                } finally {
                    setIsLoading(false);
                    fetchingSummaryRef.current.delete(currentPath.id);
                }
            }
        };
        generateSummary();
    }, [currentPath, scenarioTitle, updatePathWithSummary, kpiPriorities]);
    
    if (!currentPath) return null;
    const pathIndex = completedPaths.findIndex(p => p.id === currentPath.id);

    if (isDebriefLoading) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 flex items-center justify-center min-h-[20rem]">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className={`transition-all duration-300 ease-out ${visibleSections.debrief ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start gap-2 mb-4">
                        <Brain className="w-6 h-6 text-indigo-500 mr-1 flex-shrink-0 mt-1" />
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-xl font-semibold text-gray-900">Executive Debrief</h3>
                                <InfoTooltip>This summary analyzes the decisions and outcomes for the currently selected path, providing observations and recommendations.</InfoTooltip>
                            </div>
                            <p className="text-gray-600 mt-1">AI-Powered Analysis of Path #{pathIndex + 1}</p>
                        </div>
                    </div>
                    <div className="border-t pt-4 min-h-[10rem] prose prose-sm max-w-none w-full"> 
                        <ReactMarkdown>{currentPath?.summary || ""}</ReactMarkdown>
                    </div>
                </div>
            </div>
            
            <div style={{ transitionDelay: '200ms' }} className={`transition-all duration-300 ease-out ${visibleSections.chart ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start gap-2 mb-4">
                        <LineChart className="w-6 h-6 text-indigo-500 mr-1 flex-shrink-0 mt-1" />
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-xl font-semibold text-gray-900">Metric Trajectory</h3>
                                <InfoTooltip>This chart compares the performance of all completed paths. Use the toggles to switch between key metrics.</InfoTooltip>
                            </div>
                            <p className="text-gray-600 mt-1">Visualizing key metrics over the course of the simulation.</p>
                        </div>
                    </div>
                    <div className="border-t pt-4">
                        <PathsChart ref={chartRef} completedPaths={completedPaths} />
                    </div>
                </div>
            </div>

            <div style={{ transitionDelay: '400ms' }} className={`transition-all duration-300 ease-out ${visibleSections.comparison ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                {completedPaths.length > 1 ? (
                    <PathComparison comparison={comparisonData} isLoading={isComparisonLoading} onExport={() => onExport(rawComparisonData)} completedPaths={completedPaths} onNavigate={onNavigateToPath} />
                ) : ( <ComparisonPlaceholder onPlayAgain={onPlayAgain} /> )}
            </div>
        </div>
    );
};
